/**
 * F-70 translate UI: the dialog the toolbar's translate icon opens, the cost
 * confirmation, and the SHARED progress panel. Mounted once per board view by
 * BoardCanvas.
 *
 * What this does NOT do: write cells, or own the progress. The run is a
 * server-side JOB (the OpenRouter key never reaches the browser — docs/06 §2.5;
 * async since 2026-07-16 because a 10k-row script is ~250 model calls over many
 * minutes). Two consequences shape this file:
 *
 *  - **Progress comes from the DOC**, not from the POST. The job publishes
 *    `txJob` under the server's own origin, so the bar below renders for EVERY
 *    user on the board, survives a reload, and survives the tab that started it
 *    being closed. This component never writes that state (a tick on a user's
 *    undo stack would be C6/C8 breakage).
 *  - **The POST only starts the run** (202 + jobId). The translated CELLS
 *    arrive on their own, through the C5 guarded writers and normal sync, so
 *    the column visibly fills in for everyone.
 *
 * Three ways in (2026-09-03): the toolbar icon on a non-source column opens
 * the dialog with THAT column as the target; the toolbar icon on the Original
 * Script column and the top-right pill's translate icon open it with a target
 * PICKER — an existing column, or a NEW column named after the language,
 * minted at submit and taken back if the run never starts.
 *
 * The dialog is a MODAL rather than an F-27 pill popover (which is where the
 * background palette lives) for two reasons: the pill hides on pan/zoom and
 * closes when the selection changes, either of which would evict a half-filled
 * form; and a native <select> cannot live in the pill, whose mousedown handler
 * preventDefaults to keep focus on the grid.
 */
import {
  BGM_COLUMN_NAME,
  findColumnByName,
  MOOD_COLUMN_NAME,
  SECTION_COLUMN_NAME,
  type BoardDoc,
  type TranslationJobState,
} from "@kola/shared";
import { useEffect, useRef, useState } from "react";
import { useStore } from "zustand";

import {
  ApiError,
  cancelTranslateColumn,
  dismissTranslationJob,
  estimateTranslateColumn,
  getTranslateJob,
  startTranslateColumn,
  type TranslateEstimate,
} from "../../core/api.js";
import type { BoardSession } from "../../core/session.js";
import { Modal } from "../../ui/Modal.js";
import { ProgressBar } from "../../ui/ProgressBar.js";
import { showToast } from "../../ui/toast.js";
import {
  canSubmit,
  initialDialogState,
  submitBlock,
  submitBlockMessage,
  translateErrorMessage,
  type TranslateDialogState,
} from "./dialog-state.js";
import {
  ineligibleMessage,
  translateColumnEligibility,
  type TranslateColumnFacts,
} from "./eligibility.js";
import {
  costLines,
  isJobFor,
  isJobRunning,
  jobOutcomeMessage,
  jobView,
  needsCostConfirm,
  overRowCap,
  overRowCapMessage,
  resultSummary,
  shouldReportOutcome,
} from "./job-view.js";
import { languageLabel, languageOptions } from "./languages.js";
import { isStartingOnSheet, runKey, translateUi, type TranslateDialogTarget } from "./store.js";
import { useTranslationJob } from "./use-job.js";
// Side-effect import: registers the F-27 translate action (idempotent).
import "./action.js";

export interface TranslateLayerProps {
  session: BoardSession;
}

export function TranslateLayer({ session }: TranslateLayerProps) {
  const dialog = useStore(translateUi, (s) => s.dialog);
  const job = useTranslationJob(session);

  // A dialog must never survive leaving the board it names. A RUN deliberately
  // does not stop here: it is server-side and keeps writing into the doc (that
  // is the point of the job), and DELETE is the only way to stop it.
  useEffect(() => {
    return () => translateUi.getState().closeDialog();
  }, []);

  const forThisBoard = dialog !== null && dialog.boardId === session.boardId;

  return (
    <>
      {forThisBoard && <TranslateDialog session={session} target={dialog} job={job} />}
      {job !== undefined && <TranslateJobPanel session={session} job={job} />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Progress panel — the shared bar, from the doc                       */
/* ------------------------------------------------------------------ */

/**
 * The bar every user on the board sees, driven entirely by `txJob`. Also the
 * only place a run can be cancelled, and the only place its outcome is
 * reported.
 */
function TranslateJobPanel({
  session,
  job,
}: {
  session: BoardSession;
  job: TranslationJobState;
}) {
  const [cancelling, setCancelling] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  // F-70: report the outcome ONCE, and only to clients that actually watched
  // the run finish — see `shouldReportOutcome` (the state outlives the run, so
  // "there is a terminal job on the doc" is not news).
  const previous = useRef<TranslationJobState | undefined>(job);
  useEffect(() => {
    const before = previous.current;
    previous.current = job;
    if (!shouldReportOutcome(before, job)) return;
    const name = columnName(session, job.sheetId, job.targetColId);
    void reportOutcome(session, job.jobId, name, jobOutcomeMessage(job, name));
  }, [session, job]);

  useEffect(() => {
    // A new run must clear a stale cancel/dismiss from the previous one.
    setCancelling(false);
    setDismissing(false);
  }, [job?.jobId]);

  const view = jobView(job, columnName(session, job.sheetId, job.targetColId));
  const cancel = async () => {
    setCancelling(true);
    try {
      await cancelTranslateColumn(session.boardId, job.jobId);
    } catch (cancelError) {
      setCancelling(false);
      // 404 unknown-job = it already finished; the doc says so on its own.
      if (!(cancelError instanceof ApiError && cancelError.status === 404)) {
        showToast(translateErrorMessage(cancelError));
      }
    }
  };

  // Dismiss is SHARED, not local view state: terminal state stays on the doc so
  // the outcome can be read, which means the panel is on the board for every
  // user until something clears it. A `useState` flag hid it from this tab only
  // and died with the component — so Monday's "Finished translating into French"
  // greeted everyone who opened the board, on every reload, indefinitely (and
  // after a crash, a permanent "failed: interrupted" banner). Clearing the doc
  // key is the only dismissal that means anything.
  const dismiss = async () => {
    setDismissing(true);
    try {
      await dismissTranslationJob(session.boardId, job.jobId);
      // No local hide: the doc write comes back over the socket and this
      // component unmounts with the state it rendered from.
    } catch (dismissError) {
      setDismissing(false);
      // 409 = the state already moved on (someone else dismissed it, or a new
      // run started). The doc says so on its own.
      if (!(dismissError instanceof ApiError && dismissError.status === 409)) {
        showToast(translateErrorMessage(dismissError));
      }
    }
  };

  return (
    <div className="tx-job" role="status">
      <div className="tx-job-head">
        <span className="tx-job-title">{view.title}</span>
        {view.cancellable ? (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => void cancel()}
            disabled={cancelling}
            // The tokens for the in-flight batch are already spent, so it is
            // still written — saying so avoids "cancel did nothing" reports.
            title="Stop the run after the batch in flight"
          >
            {cancelling ? "Stopping…" : "Cancel"}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => void dismiss()}
            disabled={dismissing}
            title="Clear this result from the board for everyone"
          >
            {dismissing ? "Dismissing…" : "Dismiss"}
          </button>
        )}
      </div>
      <ProgressBar percent={view.percent} label={view.label} />
      {job.message !== undefined && job.status !== "running" && (
        <p className="tx-job-message">{job.message}</p>
      )}
    </div>
  );
}

/**
 * Toast the outcome, preferring the job API's full breakdown (translated vs
 * skipped, tokens — F-70.7) and falling back to the doc's counters.
 *
 * The fallback is NOT an edge case: the registry is in-memory, so a server
 * restart forgets a finished job and this 404s. The doc still knows
 * done/total/failed, which is a worse sentence but a true one.
 */
async function reportOutcome(
  session: BoardSession,
  jobId: string,
  name: string,
  fallback: string,
): Promise<void> {
  try {
    const record = await getTranslateJob(session.boardId, jobId);
    if (record.result === undefined) {
      showToast(record.error === undefined ? fallback : `Translation failed: ${record.error}`);
      return;
    }
    showToast(resultSummary(record.result, name));
  } catch {
    showToast(fallback);
  }
}

/**
 * The target column's name RIGHT NOW — read from the doc rather than stored on
 * the job, so a column renamed mid-run is never reported under its old name.
 * A deleted column falls back rather than rendering "undefined".
 */
function columnName(session: BoardSession, sheetId: string, colId: string): string {
  return session.board.getColumn(sheetId, colId)?.name || "the column";
}

/* ------------------------------------------------------------------ */
/* Dialog: languages → estimate → (confirm) → start                    */
/* ------------------------------------------------------------------ */

/** The cost confirmation, held while the user decides. */
interface PendingConfirm {
  estimate: TranslateEstimate;
  state: TranslateDialogState;
  /** The resolved target — an existing column, or the one minted for this run. */
  colId: string;
  /** The column this dialog minted, to take back if the user backs out. */
  minted: string | null;
}

/** The picker's "mint a column named after the language" choice. */
const NEW_COLUMN = "__new__";

/** A column as the picker lists it. */
interface TargetCandidate {
  colId: string;
  label: string;
}

/**
 * A column's name for the dialog. Unnamed columns are titled by position
 * ("Column 3"): the grid's pill shows nothing for them, but a select cannot.
 */
function columnLabel(board: BoardDoc, sheetId: string, colId: string): string {
  const name = board.getColumn(sheetId, colId)?.name ?? "";
  if (name !== "") return name;
  const index = board.sortedColIds(sheetId).indexOf(colId);
  return index >= 0 ? `Column ${index + 1}` : "this column";
}

function translateFacts(board: BoardDoc, sheetId: string): TranslateColumnFacts {
  return {
    sourceColId: board.sourceColumnId(sheetId),
    isLocked: (colId) => board.isColumnLocked(sheetId, colId),
    hasColumn: (colId) => board.hasColumn(sheetId, colId),
  };
}

/**
 * The existing columns the picker offers: every legal F-70 target EXCEPT the
 * three "Divide into sections" mints (Section, Mood, BGM) — legal in the
 * strict sense, but "Translate into Section" is not a thing anyone means.
 */
function targetCandidates(board: BoardDoc, sheetId: string): TargetCandidate[] {
  const facts = translateFacts(board, sheetId);
  const reserved = new Set<string>([SECTION_COLUMN_NAME, MOOD_COLUMN_NAME, BGM_COLUMN_NAME]);
  return board
    .sortedColIds(sheetId)
    .filter((colId) => translateColumnEligibility(sheetId, colId, facts).ok)
    .filter((colId) => !reserved.has(board.getColumn(sheetId, colId)?.name ?? ""))
    .map((colId) => ({ colId, label: columnLabel(board, sheetId, colId) }));
}

/**
 * What to call the minted column: the language's display name ("Korean"),
 * numbered ("Korean 2") when the sheet already has one — a second Korean pass
 * must not be mistaken for the first.
 */
function newColumnName(board: BoardDoc, sheetId: string, to: string): string {
  const base = languageLabel(to);
  if (findColumnByName(board, sheetId, base) === undefined) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base} ${n}`;
    if (findColumnByName(board, sheetId, candidate) === undefined) return candidate;
  }
}

function TranslateDialog({
  session,
  target,
  job,
}: {
  session: BoardSession;
  target: TranslateDialogTarget;
  job: TranslationJobState | undefined;
}) {
  const { board } = session;
  const starting = useStore(translateUi, (s) => s.starting);
  const close = () => translateUi.getState().closeDialog();

  // Picker mode (colId null): the target is chosen HERE — an existing column
  // or a new one named after the language. Candidates are read once at open,
  // like the mapping below; a column added mid-dialog shows up next time.
  const picker = target.colId === null;
  const [candidates] = useState<TargetCandidate[]>(() =>
    picker ? targetCandidates(board, target.sheetId) : [],
  );
  const [choice, setChoice] = useState<string>(() => target.colId ?? NEW_COLUMN);
  const chosenColId = choice === NEW_COLUMN ? null : choice;

  // Read once, at open: the mapping is what pre-fills the form (F-70 —
  // re-opening a translated column shows what it holds), and a remote change
  // mid-dialog must not yank the user's half-made choice out from under them.
  const [state, setState] = useState<TranslateDialogState>(() =>
    initialDialogState(
      chosenColId === null ? undefined : board.getColumnTranslation(target.sheetId, chosenColId),
    ),
  );
  const [error, setError] = useState<string | null>(null);
  const [sizing, setSizing] = useState(false);
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);

  const sourceColId = board.sourceColumnId(target.sheetId);
  // The source column's REAL name, not the literal "Original Script": F-28
  // locks the name, but a board seeded before the lock could carry another one,
  // and the dialog must name what will actually be read.
  const sourceName =
    (sourceColId === undefined ? undefined : board.getColumn(target.sheetId, sourceColId)?.name) ??
    "Original Script";
  // What the run writes into, as the dialog names it. A new column is named
  // after the language once one is chosen.
  const targetName =
    chosenColId !== null
      ? columnLabel(board, target.sheetId, chosenColId)
      : state.to === ""
        ? "a new column"
        : `a new "${languageLabel(state.to)}" column`;
  // Busy = this browser's POST is in flight, OR the doc says a job is running
  // on this BOARD (which is true even if another user started it). Per board,
  // not per column: the server keys the job registry `(boardId, sheetId)` so the
  // one `txJob` key has one writer, and it 409s a second run on ANY column —
  // gating this on `isJobFor` would offer a Translate button that always fails.
  const running = isStartingOnSheet(starting, target.boardId, target.sheetId) || isJobRunning(job);
  const runningHere =
    running && chosenColId !== null && isJobFor(job, target.sheetId, chosenColId);
  const block = submitBlock(state);

  const pickTarget = (next: string) => {
    setChoice(next);
    setError(null);
    // An existing column translated before brings its pair along — the same
    // "re-open shows what it holds" rule as opening it from the toolbar.
    if (next === NEW_COLUMN) return;
    const mapping = board.getColumnTranslation(target.sheetId, next);
    if (mapping !== undefined) {
      setState((prev) => ({ ...prev, from: mapping.from, to: mapping.to }));
    }
  };

  /** Take back a column this dialog minted and then could not use (still empty). */
  const discardMinted = (minted: string | null) => {
    if (minted !== null) session.deleteColumn(target.sheetId, minted);
  };

  const start = async (chosen: TranslateDialogState, colId: string): Promise<void> => {
    const key = runKey({ boardId: target.boardId, sheetId: target.sheetId, colId });
    translateUi.getState().markStarting(key);
    close();
    try {
      await startTranslateColumn(session.boardId, {
        sheetId: target.sheetId,
        targetColId: colId,
        from: chosen.from,
        to: chosen.to,
        retranslate: chosen.retranslate,
      });
      // Nothing else to do: the job publishes its progress onto the doc, and
      // TranslateJobPanel renders it — for this user and everyone else.
    } catch (startError) {
      // 409 = someone (or this user, twice) already started it. The doc's bar
      // is already showing that run, so this is a notice, not a failure.
      showToast(translateErrorMessage(startError));
    } finally {
      translateUi.getState().clearStarting(key);
    }
  };

  const submit = async () => {
    if (running || sizing) return; // C5.3: one run per board — the server 409s too
    if (block !== null) {
      setError(submitBlockMessage(block));
      return;
    }
    setSizing(true);
    try {
      // Resolve the target. A NEW column is minted now — named after the
      // language, at the right of the grid — and taken back if the run never
      // starts, so a refused estimate does not leave an empty column behind.
      let colId = chosenColId;
      let minted: string | null = null;
      if (colId === null) {
        minted = session.insertNamedColumn(
          target.sheetId,
          newColumnName(board, target.sheetId, state.to),
        );
        if (minted === null) {
          setError("Offline — the new column couldn't be added. Reconnect and try again.");
          return;
        }
        colId = minted;
        // The server plans against ITS copy of the doc: let the column land
        // there before asking about it.
        await session.whenFlushed();
      }

      // C4: the ids were captured when the dialog opened. Re-check them
      // against the doc at click time — someone may have deleted the column
      // or the source since. The server re-checks everything again anyway (it
      // never trusts the request), so this is purely about a clear message.
      const eligibility = translateColumnEligibility(
        target.sheetId,
        colId,
        translateFacts(board, target.sheetId),
      );
      if (!eligibility.ok) {
        setError(ineligibleMessage(eligibility.reason));
        discardMinted(minted);
        return;
      }

      // F-70 COST GUARD. The estimate is a server-side plan + arithmetic (no
      // provider call), so asking always is cheap — and it is the only way to
      // know whether this run is small enough to start without asking.
      let estimate: TranslateEstimate;
      try {
        estimate = await estimateTranslateColumn(session.boardId, {
          sheetId: target.sheetId,
          targetColId: colId,
          from: state.from,
          to: state.to,
          // Must match the start request, or we quote a different run than
          // the one that executes.
          retranslate: state.retranslate,
        });
      } catch (sizeError) {
        // FAIL CLOSED: without a size, a 10k-row run would start unconfirmed.
        setError(translateErrorMessage(sizeError));
        discardMinted(minted);
        return;
      }

      // Before the price, not after it: the estimate is sized without the
      // server's row cap, so it will quote a run the start route then refuses.
      if (overRowCap(estimate)) {
        setError(overRowCapMessage(estimate));
        discardMinted(minted);
        return;
      }
      if (needsCostConfirm(estimate)) {
        setConfirm({ estimate, state, colId, minted });
        return;
      }
      await start(state, colId);
    } finally {
      setSizing(false);
    }
  };

  if (confirm !== null) {
    return (
      <CostConfirm
        estimate={confirm.estimate}
        columnName={columnLabel(board, target.sheetId, confirm.colId)}
        onBack={() => {
          // Backing out of a run into a column minted for it takes the column
          // back too — it is empty, and only existed for this run.
          discardMinted(confirm.minted);
          setConfirm(null);
        }}
        onConfirm={() => void start(confirm.state, confirm.colId)}
      />
    );
  }

  return (
    <Modal title={picker ? `Translate ${sourceName}` : `Translate into ${targetName}`} onClose={close}>
      <form
        className="tx-dialog"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {/* F-70/F-28: there is no source picker — say plainly what gets read. */}
        <p className="tx-source-note">
          Translates the whole <strong>{sourceName}</strong> column, row by row, into{" "}
          <strong>{targetName}</strong>.
        </p>
        {picker && (
          <label className="tx-field">
            <span>Into</span>
            <select value={choice} onChange={(event) => pickTarget(event.target.value)}>
              <option value={NEW_COLUMN}>New column</option>
              {candidates.map((candidate) => (
                <option key={candidate.colId} value={candidate.colId}>
                  {candidate.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="tx-field">
          <span>From</span>
          <select
            value={state.from}
            onChange={(event) => {
              setState((prev) => ({ ...prev, from: event.target.value }));
              setError(null);
            }}
          >
            {languageOptions(state.from, true).map((language) => (
              <option key={language.tag} value={language.tag}>
                {language.label}
              </option>
            ))}
          </select>
        </label>
        <label className="tx-field">
          <span>To</span>
          <select
            value={state.to}
            autoFocus
            onChange={(event) => {
              setState((prev) => ({ ...prev, to: event.target.value }));
              setError(null);
            }}
          >
            <option value="">Choose a language…</option>
            {languageOptions(state.to, false).map((language) => (
              <option key={language.tag} value={language.tag}>
                {language.label}
              </option>
            ))}
          </select>
        </label>
        {picker && chosenColId === null && (
          <p className="tx-hint">
            The new column is added at the right of the grid and named after the language.
          </p>
        )}
        {/* F-70.2's two triggers. Unchecked (the default) is a RESUME: only the
            rows that still need it, which is what makes re-running after a
            cancel continue rather than re-buy the column. */}
        <label className="tx-check">
          <input
            type="checkbox"
            checked={state.retranslate}
            onChange={(event) => {
              setState((prev) => ({ ...prev, retranslate: event.target.checked }));
              setError(null);
            }}
          />
          <span>
            Re-translate rows that already have a translation
            <em> — off, only untranslated rows are sent. Your own edits are never overwritten.</em>
          </span>
        </label>
        {error !== null && (
          <p className="tx-error" role="alert">
            {error}
          </p>
        )}
        {running && (
          <p className="tx-error" role="status">
            {runningHere
              ? "This column is already being translated."
              : `Another column on this board is being translated. Wait for that run to finish${
                  job === undefined ? "" : ` — see the progress bar for ${
                    board.getColumn(job.sheetId, job.targetColId)?.name || "the column"
                  }`
                }.`}
          </p>
        )}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!canSubmit(state) || running || sizing}
          >
            {sizing ? "Checking size…" : "Translate"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * F-70's cost guard: past ~500 rows, the run's size and price are shown and
 * must be confirmed. This is the last screen before money is spent, so it says
 * what will be bought (rows, tokens, model) rather than "Are you sure?".
 */
function CostConfirm({
  estimate,
  columnName,
  onBack,
  onConfirm,
}: {
  estimate: TranslateEstimate;
  columnName: string;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const lines = costLines(estimate);
  return (
    <Modal title={`Translate ${lines.rows} into ${columnName}?`} onClose={onBack}>
      <div className="tx-dialog">
        <p className="tx-source-note">
          This is a large run. It happens on the server and keeps going if you close the tab — you
          can watch the progress and cancel it at any time.
        </p>
        <dl className="tx-cost">
          <div>
            <dt>Rows</dt>
            <dd>{lines.rows}</dd>
          </div>
          <div>
            <dt>Tokens</dt>
            <dd>{lines.tokens}</dd>
          </div>
          <div>
            <dt>Cost</dt>
            {/* Never guessed: a model with no published price says so rather
                than showing a number nobody can stand behind. */}
            <dd>{lines.cost ?? "unknown for this model"}</dd>
          </div>
          <div>
            <dt>Model</dt>
            <dd>{lines.model}</dd>
          </div>
        </dl>
        <p className="tx-cost-note">Estimated — the real total depends on the text.</p>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onBack}>
            Back
          </button>
          <button type="button" className="btn btn-primary" onClick={onConfirm}>
            Translate {lines.rows}
          </button>
        </div>
      </div>
    </Modal>
  );
}
